// src/pages/EventManager.jsx
// Event Creator + console (managers). Create an event/trip (auto-creates a
// Google Chat space and seeds chaperones), then manage it: two-checkpoint
// attendance (Bus #1 boarding + return boarding), a QR viewer that toggles
// between the Bus #1 and Return boarding QRs for a chaperone to hold up, MANUAL
// boarding (type-ahead against /students, for kids who can't scan), printable
// sign-in sheet and return card, and cancel. Chaperones and the SOC also see
// their trips here under "My Trips". Gated by `event_manager` (event_admin and
// superadmin also pass).
//
// Note: the data checkpoints are still keyed "checkin" (present/presentAt) and
// "board" (boarded/boardedAt) — only the on-screen labels changed, so no data
// migration is needed.
//
// Deps: qrcode (already installed for Trip Spaces).

import React, { useEffect, useState, useCallback } from "react";
import QRCode from "qrcode";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  collection, query, where, onSnapshot, getDocs,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { useAuth } from "../contexts/AuthContext";
import { downloadEventSignInSheet, downloadEventBoardingCard } from "../utils/eventRosterPdf";
import { downloadEventBarcodeSheet, eventBarcodeSheetBase64 } from "../utils/eventBarcodeSheet";
import EventReports from "./EventReports";

const KA_RED = "#E83530";
const KA_NAVY = "#233C5F";
const KA_GOLD = "#A29164";

// datetime-local default: now + offset hours, in the browser's local time.
function localDT(offsetHours = 0) {
  const d = new Date(Date.now() + offsetHours * 3600 * 1000);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Firestore Timestamp (or millis) -> a datetime-local value in browser-local
// time, for pre-filling the edit form's date inputs.
function tsToLocalDT(ts) {
  const ms = ts?.toMillis ? ts.toMillis() : (typeof ts === "number" ? ts : null);
  if (!ms) return "";
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Build the join URL for a checkpoint. Boarding carries &c=board.
function joinUrlFor(ev, kind) {
  const base = `${window.location.origin}/trip-join?s=${ev.joinToken}`;
  return kind === "board" ? `${base}&c=board` : base;
}

// Best-effort display name from a /students doc of unknown exact shape.
function studentName(s) {
  return (
    s.name ||
    [s.firstName, s.lastName].filter(Boolean).join(" ") ||
    s.preferredName || s.displayName || s.fullName ||
    (s.email ? s.email.split("@")[0] : "")
  );
}

export default function EventManager() {
  // allowedTools is surfaced on the auth context itself (from the Firestore
  // profile) — NOT on the raw `user` (the Firebase auth user, which has no
  // allowedTools). Reading user.allowedTools is what denied granted managers.
  const { user, allowedTools = [], isSuperAdmin } = useAuth();
  const tools = allowedTools;
  // Creation/management tier: superadmin, event_admin, or event_manager.
  const canCreate = isSuperAdmin || tools.includes("event_manager") || tools.includes("event_admin");
  const isAdmin = isSuperAdmin || tools.includes("event_admin");
  // Any signed-in faculty can open this tool to see trips they're chaperoning,
  // even without a create grant. Chaperone membership is matched by email.
  const myEmail = (user?.email || "").toLowerCase();

  const [form, setForm] = useState({
    name: "",
    description: "",
    startAt: localDT(24),
    endAt: localDT(30),
    chatExpiryAt: localDT(54),
    capacity: "",
    gradYears: "",
    chaperones: "",
    soc: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [qr, setQr] = useState("");
  const [events, setEvents] = useState([]);
  const [myTrips, setMyTrips] = useState([]);      // trips where I'm a chaperone
  const [pdfBusy, setPdfBusy] = useState("");      // `${id}:sheet` | `${id}:card`
  const [reportEvent, setReportEvent] = useState(null);
  const [reportRows, setReportRows] = useState([]);

  // QR viewer modal: { ev, kind } where kind is "checkin" | "board".
  const [qrModal, setQrModal] = useState(null);
  const [qrImg, setQrImg] = useState("");

  // Cross-event reporting overlay.
  const [reportsOpen, setReportsOpen] = useState(false);

  // Manual attendance (type-ahead against /students).
  const [students, setStudents] = useState([]);
  const [studentsLoaded, setStudentsLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [manualBusy, setManualBusy] = useState("");
  const [socBusy, setSocBusy] = useState(false);

  // Add-chaperone from the roster screen.
  const [chapAddSearch, setChapAddSearch] = useState("");
  const [chapAddBusy, setChapAddBusy] = useState(false);

  // Edit-event modal.
  const [editEvent, setEditEvent] = useState(null);
  const [editForm, setEditForm] = useState(null);         // same field shape as create
  const [editChaperones, setEditChaperones] = useState([]); // [{ email, name }]
  const [editSoc, setEditSoc] = useState(null);            // { email, name } | null
  const [editSearch, setEditSearch] = useState("");
  const [editSocSearch, setEditSocSearch] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState("");

  // Chaperone picker (type-ahead against /staff) for the create form.
  const [staff, setStaff] = useState([]);
  const [staffLoaded, setStaffLoaded] = useState(false);
  const [chapSearch, setChapSearch] = useState("");
  const [chaperonesList, setChaperonesList] = useState([]); // [{ email, name }]

  // SOC picker (single select against /staff).
  const [socSelected, setSocSelected] = useState(null); // { email, name } | null
  const [socSearch, setSocSearch] = useState("");

  // Live list: superadmin / event_admin see all active events; managers see
  // their own. Single-field equality only (no composite index) — filter and
  // sort in the browser.
  useEffect(() => {
    if (!user?.uid || !canCreate) return;
    const base = collection(db, "events");
    const q = isAdmin
      ? query(base, where("status", "==", "active"))
      : query(base, where("createdBy", "==", user.uid));
    const unsub = onSnapshot(
      q,
      (snap) => {
        let list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (!isAdmin) list = list.filter((e) => e.status === "active");
        list.sort((a, b) => {
          const am = a.startAt?.toMillis ? a.startAt.toMillis() : 0;
          const bm = b.startAt?.toMillis ? b.startAt.toMillis() : 0;
          return am - bm;
        });
        setEvents(list);
      },
      (e) => console.error("events list error", e)
    );
    return () => unsub();
  }, [user?.uid, canCreate, isAdmin]);

  // Trips the current user is on — shown to EVERY faculty member who opens the
  // tool, grant or not. Includes trips where they're a chaperone OR the SOC.
  // Single-field query (status == active) with a client-side email filter, so no
  // index and no chaperoneEmails array is needed, and existing events work
  // without a re-save.
  const amChaperone = (e) =>
    Array.isArray(e.chaperones) &&
    e.chaperones.some((c) => (c.email || "").toLowerCase() === myEmail);
  const amSoc = (e) => !!(e.soc && (e.soc.email || "").toLowerCase() === myEmail);
  useEffect(() => {
    if (!myEmail) return;
    const qy = query(collection(db, "events"), where("status", "==", "active"));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((e) => amChaperone(e) || amSoc(e))
          .sort((a, b) => (a.startAt?.toMillis?.() || 0) - (b.startAt?.toMillis?.() || 0));
        setMyTrips(list);
      },
      (e) => console.error("my trips error", e)
    );
    return () => unsub();
  }, [myEmail]);

  // Live attendance for the open event — updates as students scan or are
  // marked manually.
  useEffect(() => {
    if (!reportEvent) { setReportRows([]); return; }
    setSearch(""); setChapAddSearch("");
    const unsub = onSnapshot(
      collection(db, `events/${reportEvent.id}/signups`),
      (snap) => setReportRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.error("attendance listen error", e)
    );
    return () => unsub();
  }, [reportEvent]);

  // Load the student roster once, the first time an attendance panel opens, so
  // the manual search can guess names as you type (client-side filter, like the
  // app's other search bars — no composite index, no query per keystroke).
  useEffect(() => {
    if (!reportEvent || studentsLoaded) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "students"));
        const list = snap.docs.map((d) => {
          const s = d.data() || {};
          return {
            id: d.id,
            name: studentName(s) || d.id,
            email: (s.email || "").toLowerCase(),
            sisId: s.sisId ? String(s.sisId) : "",
            grade: s.grade || s.gradeLevel || s.gradYear || s.classOf || "",
          };
        });
        list.sort((a, b) => a.name.localeCompare(b.name));
        setStudents(list);
        setStudentsLoaded(true);
      } catch (e) {
        console.error("student roster load failed", e);
      }
    })();
  }, [reportEvent, studentsLoaded]);

  // Load the staff directory once (managers only) so the chaperone picker can
  // guess names as you type. Faculty may read /staff per the Firestore rules.
  useEffect(() => {
    if (staffLoaded) return;
    if (!canCreate && !reportEvent) return; // load when the form OR a roster needs it
    (async () => {
      try {
        const snap = await getDocs(collection(db, "staff"));
        const list = snap.docs
          .map((d) => {
            const s = d.data() || {};
            return {
              id: d.id,
              name: studentName(s) || (s.email ? s.email.split("@")[0] : d.id),
              email: (s.email || "").toLowerCase(),
            };
          })
          .filter((s) => s.email);
        list.sort((a, b) => a.name.localeCompare(b.name));
        setStaff(list);
        setStaffLoaded(true);
      } catch (e) {
        console.error("staff load failed", e);
      }
    })();
  }, [canCreate, reportEvent, staffLoaded]);

  const renderQr = useCallback(async (url) => {
    try {
      setQr(await QRCode.toDataURL(url, { width: 320, margin: 2, color: { dark: KA_NAVY, light: "#FFFFFF" } }));
    } catch { setQr(""); }
  }, []);

  // Open the QR viewer for an event at a given checkpoint and render it big.
  const openQr = useCallback(async (ev, kind = "checkin") => {
    setQrModal({ ev, kind });
    try {
      setQrImg(await QRCode.toDataURL(joinUrlFor(ev, kind), {
        width: 520, margin: 2,
        color: { dark: kind === "board" ? KA_RED : KA_NAVY, light: "#FFFFFF" },
      }));
    } catch { setQrImg(""); }
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addChaperone = (c) => {
    const email = (c.email || "").toLowerCase();
    if (!email) return;
    setChaperonesList((list) =>
      list.some((x) => x.email === email) ? list : [...list, { email, name: c.name || "" }]
    );
    setChapSearch("");
  };
  const removeChaperone = (email) =>
    setChaperonesList((list) => list.filter((x) => x.email !== email));

  const parseGradYears = (raw) =>
    raw.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(""); setResult(null); setQr("");
    if (!form.name.trim()) { setError("Give the event a name."); return; }
    setBusy(true);
    try {
      const fns = getFunctions(undefined, "us-central1");
      const createEvent = httpsCallable(fns, "createEvent");
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        startAt: new Date(form.startAt).toISOString(),
        endAt: new Date(form.endAt).toISOString(),
        chatExpiryAt: new Date(form.chatExpiryAt).toISOString(),
        capacity: form.capacity === "" ? null : Number(form.capacity),
        gradYears: form.gradYears.trim() ? parseGradYears(form.gradYears) : null,
        chaperones: chaperonesList,
        soc: socSelected ? { email: socSelected.email, name: socSelected.name || "" } : null,
      };
      const { data } = await createEvent(payload);
      setResult({ ...data, name: form.name.trim() });
      await renderQr(data.joinUrl);
      setForm((f) => ({ ...f, name: "", description: "", capacity: "", gradYears: "", chaperones: "", soc: "" }));
      setChaperonesList([]); setChapSearch("");
      setSocSelected(null); setSocSearch("");
    } catch (err) {
      setError(err?.message || "Could not create the event.");
    } finally {
      setBusy(false);
    }
  };

  // Manual check-in / boarding. Pass either { signupId } (existing row) or
  // { student } (identity). busyKey drives the per-button spinner.
  const markManual = async (args, busyKey) => {
    if (!reportEvent) return;
    setManualBusy(busyKey);
    try {
      const fns = getFunctions(undefined, "us-central1");
      const { data } = await httpsCallable(fns, "markAttendance")({ eventId: reportEvent.id, ...args });
      if (args.student) setSearch("");
      if (data?.status === "waitlisted") {
        const who = args.student?.name || "That student";
        alert(`${who} was added to the waitlist — the trip is at capacity.`);
      }
    } catch (e) {
      alert(e?.message || "Could not update attendance.");
    } finally {
      setManualBusy("");
    }
  };

  // Remove someone from the roster (manager/chaperone), before or during a trip.
  const removeRow = async (signupId, label, busyKey) => {
    if (!reportEvent) return;
    if (!window.confirm(`Remove ${label || "this person"} from the roster?`)) return;
    setManualBusy(busyKey);
    try {
      const fns = getFunctions(undefined, "us-central1");
      await httpsCallable(fns, "removeFromRoster")({ eventId: reportEvent.id, signupId });
    } catch (e) {
      alert(e?.message || "Could not remove from the roster.");
    } finally {
      setManualBusy("");
    }
  };

  const downloadSheet = async (ev) => {
    setPdfBusy(`${ev.id}:sheet`);
    try {
      const qrDataUrl = await QRCode.toDataURL(joinUrlFor(ev, "checkin"), { width: 400, margin: 1 });
      const snap = await getDocs(collection(db, `events/${ev.id}/signups`));
      downloadEventSignInSheet(ev, snap.docs.map((d) => d.data()), qrDataUrl);
    } catch (err) {
      alert(err?.message || "Could not build the sign-in sheet.");
    } finally {
      setPdfBusy("");
    }
  };

  const downloadBoardingCard = async (ev) => {
    setPdfBusy(`${ev.id}:card`);
    try {
      const qrDataUrl = await QRCode.toDataURL(joinUrlFor(ev, "board"), {
        width: 600, margin: 1, color: { dark: KA_RED, light: "#FFFFFF" },
      });
      downloadEventBoardingCard(ev, qrDataUrl);
    } catch (err) {
      alert(err?.message || "Could not build the boarding card.");
    } finally {
      setPdfBusy("");
    }
  };

  // Gate sign-out barcode sheet: Code128 of each confirmed student's SIS ID.
  // Resolve sisId from the loaded /students list by email (fallback to the
  // row's own sisId). Uses the open roster (reportRows) + students list.
  // Confirmed students with resolved SIS IDs, for the barcode sheet. Uses the
  // open roster (reportRows) + loaded /students list.
  const barcodeRows = () => {
    const byEmail = new Map(students.map((s) => [s.email, s]));
    return reportRows
      .filter((r) => r.status === "confirmed" && r.role !== "staff" && r.chaperone !== true)
      .map((r) => {
        const st = r.email ? byEmail.get(r.email.toLowerCase()) : null;
        const sisId = (st && (st.sisId || st.id)) || r.sisId || "";
        return { name: r.name || r.email || "", sisId: String(sisId || "") };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const downloadBarcodes = (ev) => {
    try { downloadEventBarcodeSheet(ev, barcodeRows()); }
    catch (e) { alert(e?.message || "Could not build the barcode sheet."); }
  };

  // Chaperone confirms the list is complete and sends the sign-out sheet to the
  // SOC (generated here, uploaded + emailed by the sendSignoutToSoc function).
  const sendToSoc = async (ev) => {
    if (!ev.soc || !ev.soc.email) { alert("No SOC is assigned to this trip."); return; }
    if (!window.confirm(
      `Send the sign-out sheet to the SOC (${ev.soc.name || ev.soc.email})?\n\n` +
      `Make sure the student list is complete — this is their gate manifest.`
    )) return;
    setSocBusy(true);
    try {
      const pdfBase64 = eventBarcodeSheetBase64(ev, barcodeRows());
      const fns = getFunctions(undefined, "us-central1");
      await httpsCallable(fns, "sendSignoutToSoc")({ eventId: ev.id, pdfBase64 });
      alert(`Sign-out sheet sent to ${ev.soc.name || ev.soc.email}.`);
    } catch (e) {
      alert(e?.message || "Could not send the sign-out sheet to the SOC.");
    } finally {
      setSocBusy(false);
    }
  };

  // Add a chaperone from the roster screen (type-ahead against /staff).
  const addChaperoneToRoster = async (s) => {
    if (!reportEvent) return;
    setChapAddBusy(true);
    try {
      const fns = getFunctions(undefined, "us-central1");
      await httpsCallable(fns, "addChaperone")({ eventId: reportEvent.id, chaperone: { email: s.email, name: s.name } });
      setChapAddSearch("");
    } catch (e) {
      alert(e?.message || "Could not add the chaperone.");
    } finally {
      setChapAddBusy(false);
    }
  };

  const openEdit = (ev) => {
    setEditEvent(ev);
    setEditForm({
      name: ev.name || "",
      description: ev.description || "",
      startAt: tsToLocalDT(ev.startAt),
      endAt: tsToLocalDT(ev.endAt),
      chatExpiryAt: tsToLocalDT(ev.chatExpiryAt),
      capacity: ev.capacity != null ? String(ev.capacity) : "",
      gradYears: Array.isArray(ev.gradYears) ? ev.gradYears.join(" ") : "",
    });
    setEditChaperones((ev.chaperones || []).map((c) => ({ email: c.email, name: c.name || "" })));
    setEditSoc(ev.soc ? { email: ev.soc.email, name: ev.soc.name || "" } : null);
    setEditSearch(""); setEditSocSearch(""); setEditError("");
  };

  const setEdit = (k) => (e) => setEditForm((f) => ({ ...f, [k]: e.target.value }));

  const saveEdit = async () => {
    if (!editEvent || !editForm) return;
    if (!editForm.name.trim()) { setEditError("Give the event a name."); return; }
    setEditBusy(true); setEditError("");
    try {
      const fns = getFunctions(undefined, "us-central1");
      const patch = {
        name: editForm.name.trim(),
        description: editForm.description.trim(),
        startAt: new Date(editForm.startAt).toISOString(),
        endAt: new Date(editForm.endAt).toISOString(),
        chatExpiryAt: new Date(editForm.chatExpiryAt).toISOString(),
        capacity: editForm.capacity === "" ? null : Number(editForm.capacity),
        gradYears: editForm.gradYears.trim() ? parseGradYears(editForm.gradYears) : null,
        soc: editSoc ? { email: editSoc.email, name: editSoc.name || "" } : null,
      };
      await httpsCallable(fns, "updateEvent")({ eventId: editEvent.id, patch, chaperones: editChaperones });
      setEditEvent(null); setEditForm(null);
    } catch (e) {
      setEditError(e?.message || "Could not save changes.");
    } finally {
      setEditBusy(false);
    }
  };

  const cancelEvent = async (ev) => {
    if (!window.confirm(`Cancel "${ev.name}" and delete its chat space now? (Messages stay in Vault.)`)) return;
    try {
      const fns = getFunctions(undefined, "us-central1");
      await httpsCallable(fns, "cancelEvent")({ eventId: ev.id });
    } catch (err) {
      alert(err?.message || "Could not cancel the event.");
    }
  };

  const fmt = (ts) => {
    try {
      const d = ts?.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleString("en-US", { timeZone: "Asia/Amman", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return ""; }
  };

  return (
    <div style={{ padding: 32, maxWidth: 900, margin: "0 auto" }}>
      <h2 style={{ color: KA_NAVY, marginBottom: 4 }}>{canCreate ? "Events" : "My Trips"}</h2>
      <p style={{ color: "#666", marginTop: 0 }}>
        {canCreate
          ? <>Create an event or trip. A private Google Chat space is created automatically and your chaperones are added. Students sign up at <code>/events</code> and scan to check in and board.</>
          : "Trips you're chaperoning or on call for as SOC. Open a trip's roster to put students on Bus #1, scan return boarding, add walk-ups, pull the gate barcode sheet, or remove someone."}
      </p>

      {canCreate && (<>
      <form onSubmit={handleCreate} style={card}>
        <div style={grid2}>
          <L label="Event name">
            <input value={form.name} onChange={set("name")} placeholder="e.g. Grade 10 Wadi Rum" style={inp} />
          </L>
          <L label="Capacity (blank = unlimited)">
            <input type="number" min="1" value={form.capacity} onChange={set("capacity")} placeholder="e.g. 40" style={inp} />
          </L>
        </div>
        <L label="Description">
          <textarea value={form.description} onChange={set("description")} rows={2} style={{ ...inp, resize: "vertical" }} />
        </L>
        <div style={grid3}>
          <L label="Starts"><input type="datetime-local" value={form.startAt} onChange={set("startAt")} style={inp} /></L>
          <L label="Ends"><input type="datetime-local" value={form.endAt} onChange={set("endAt")} style={inp} /></L>
          <L label="Chat expires"><input type="datetime-local" value={form.chatExpiryAt} onChange={set("chatExpiryAt")} style={inp} /></L>
        </div>
        <div style={grid2}>
          <L label="Grad years allowed (blank = all)">
            <input value={form.gradYears} onChange={set("gradYears")} placeholder="e.g. 27 28" style={inp} />
          </L>
          <L label="Chaperones (type a name)">
            <ChaperonePicker
              staff={staff}
              staffLoaded={staffLoaded}
              selected={chaperonesList}
              search={chapSearch}
              setSearch={setChapSearch}
              onAdd={addChaperone}
              onRemove={removeChaperone}
            />
          </L>
        </div>
        <div style={grid2}>
          <L label="SOC — School Officer in Charge (gate sign-out)">
            <SocPicker
              staff={staff}
              staffLoaded={staffLoaded}
              selected={socSelected}
              search={socSearch}
              setSearch={setSocSearch}
              onPick={(c) => { setSocSelected({ email: (c.email || "").toLowerCase(), name: c.name || "" }); setSocSearch(""); }}
              onClear={() => setSocSelected(null)}
            />
          </L>
          <div />
        </div>
        <button type="submit" disabled={busy} style={{ ...btn, background: busy ? "#aaa" : KA_RED }}>
          {busy ? "Creating…" : "Create event"}
        </button>
      </form>

      {error && <div style={{ color: KA_RED, marginTop: 12 }}>{error}</div>}

      {result && (
        <div style={{ ...card, borderColor: KA_GOLD, display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center" }}>
          {qr && <img src={qr} alt="Check-in QR" style={{ width: 200, height: 200 }} />}
          <div style={{ flex: "1 1 240px" }}>
            <h3 style={{ margin: "0 0 8px", color: KA_NAVY }}>{result.name}</h3>
            <p style={{ fontSize: 13, color: "#555", margin: "0 0 8px" }}>
              Chat space created and chaperones added. This is the <strong>Bus #1</strong> QR —
              open the trip below to switch to the return boarding QR.
            </p>
            <div style={{ fontSize: 12, wordBreak: "break-all", color: "#888" }}>{result.joinUrl}</div>
            {result.spaceUri && (
              <a href={result.spaceUri} target="_blank" rel="noreferrer" style={{ ...ghost, display: "inline-block", marginTop: 12 }}>Open space</a>
            )}
          </div>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 36, gap: 12, flexWrap: "wrap" }}>
        <h3 style={{ color: KA_NAVY, margin: 0 }}>{isAdmin ? "All active events" : "Your active events"}</h3>
        <button onClick={() => setReportsOpen(true)} style={{ ...ghost, color: KA_GOLD, borderColor: KA_GOLD }}>📊 Reports</button>
      </div>
      {events.length === 0 && <p style={{ color: "#888", fontSize: 14 }}>No active events yet.</p>}
      <div style={{ display: "grid", gap: 10 }}>
        {events.map((ev) => (
          <div key={ev.id} style={row}>
            <div>
              <div style={{ fontWeight: 600, color: KA_NAVY }}>{ev.name}</div>
              <div style={{ fontSize: 12, color: "#999" }}>
                {fmt(ev.startAt)} → {fmt(ev.endAt)} · chat ends {fmt(ev.chatExpiryAt)}
                {ev.capacity ? ` · cap ${ev.capacity} students` : " · no cap"}
                {` · ${ev.studentCount ?? ev.signedUpCount ?? 0} students`}
                {ev.staffCount ? ` · ${ev.staffCount} staff` : ""}
                {ev.soc ? ` · SOC: ${ev.soc.name || ev.soc.email}` : ""}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button onClick={() => setReportEvent(ev)} style={{ ...ghost, background: KA_NAVY, color: "#fff" }}>Roster</button>
              <button onClick={() => openQr(ev, "checkin")} style={ghost}>QR</button>
              <button onClick={() => downloadSheet(ev)} style={ghost} disabled={pdfBusy === `${ev.id}:sheet`}>
                {pdfBusy === `${ev.id}:sheet` ? "…" : "Sign-in sheet"}
              </button>
              <button onClick={() => downloadBoardingCard(ev)} style={{ ...ghost, color: KA_RED, borderColor: KA_RED }} disabled={pdfBusy === `${ev.id}:card`}>
                {pdfBusy === `${ev.id}:card` ? "…" : "Return card"}
              </button>
              <button onClick={() => openEdit(ev)} style={ghost}>Edit</button>
              <button onClick={() => cancelEvent(ev)} style={{ ...ghost, color: KA_RED, borderColor: KA_RED }}>Cancel</button>
            </div>
          </div>
        ))}
      </div>
      </>)}

      {/* ---- My Trips — shown to every faculty member (chaperone or SOC) ---- */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: canCreate ? 36 : 8, gap: 12, flexWrap: "wrap" }}>
        <h3 style={{ color: KA_NAVY, margin: 0 }}>My Trips</h3>
      </div>
      {myTrips.length === 0 && <p style={{ color: "#888", fontSize: 14 }}>You're not listed on any active trips.</p>}
      <div style={{ display: "grid", gap: 10 }}>
        {myTrips.map((ev) => {
          const chap = amChaperone(ev);
          const soc = amSoc(ev);
          return (
          <div key={ev.id} style={row}>
            <div>
              <div style={{ fontWeight: 600, color: KA_NAVY, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {ev.name}
                {chap && <span style={roleBadge(KA_NAVY)}>Chaperone</span>}
                {soc && <span style={roleBadge(KA_GOLD)}>SOC</span>}
              </div>
              <div style={{ fontSize: 12, color: "#999" }}>
                {fmt(ev.startAt)} → {fmt(ev.endAt)}
                {ev.capacity ? ` · cap ${ev.capacity} students` : " · no cap"}
                {` · ${ev.studentCount ?? 0} students`}
                {ev.soc ? ` · SOC: ${ev.soc.name || ev.soc.email}` : ""}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button onClick={() => setReportEvent(ev)} style={{ ...ghost, background: KA_NAVY, color: "#fff" }}>Roster</button>
              <button onClick={() => openQr(ev, "checkin")} style={ghost}>QR</button>
              <button onClick={() => downloadSheet(ev)} style={ghost} disabled={pdfBusy === `${ev.id}:sheet`}>
                {pdfBusy === `${ev.id}:sheet` ? "…" : "Sign-in sheet"}
              </button>
              <button onClick={() => downloadBoardingCard(ev)} style={{ ...ghost, color: KA_RED, borderColor: KA_RED }} disabled={pdfBusy === `${ev.id}:card`}>
                {pdfBusy === `${ev.id}:card` ? "…" : "Return card"}
              </button>
            </div>
          </div>
          );
        })}
      </div>

      {/* ---- QR viewer: toggle Bus #1 <-> Return boarding, hold screen up to scan ---- */}
      {qrModal && (() => {
        const { ev, kind } = qrModal;
        const isBoard = kind === "board";
        const accent = isBoard ? KA_RED : KA_NAVY;
        return (
          <div style={overlay} onClick={() => setQrModal(null)}>
            <div style={{ ...modal, maxWidth: 440, textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <h3 style={{ margin: 0, color: KA_NAVY, fontSize: 18 }}>{ev.name}</h3>
                <button onClick={() => setQrModal(null)} style={ghost}>Close</button>
              </div>

              {/* Toggle */}
              <div style={{ display: "flex", gap: 8, justifyContent: "center", margin: "6px 0 14px" }}>
                <button
                  onClick={() => openQr(ev, "checkin")}
                  style={{ ...toggleBtn, ...(isBoard ? {} : { background: KA_NAVY, color: "#fff", borderColor: KA_NAVY }) }}
                >
                  Bus #1
                </button>
                <button
                  onClick={() => openQr(ev, "board")}
                  style={{ ...toggleBtn, ...(isBoard ? { background: KA_RED, color: "#fff", borderColor: KA_RED } : {}) }}
                >
                  Return boarding
                </button>
              </div>

              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: accent, marginBottom: 10 }}>
                {isBoard ? "Scan for return boarding" : "Scan to board Bus #1"}
              </div>

              {qrImg
                ? <img src={qrImg} alt={isBoard ? "Return boarding QR" : "Bus #1 QR"} style={{ width: 300, height: 300, maxWidth: "100%" }} />
                : <div style={{ color: "#999", padding: 40 }}>Generating…</div>}

              <div style={{ fontSize: 11, wordBreak: "break-all", color: "#aaa", marginTop: 12 }}>
                {joinUrlFor(ev, kind)}
              </div>
              <p style={{ fontSize: 12, color: "#777", marginTop: 10 }}>
                {isBoard
                  ? "Hold this up at the return bus door. Each student scans to confirm they're on the return bus — it logs a separate time from Bus #1."
                  : "Hold this up as students get on Bus #1, or print the sign-in sheet. Scanning puts the student on Bus #1 and adds them to the chat."}
              </p>
            </div>
          </div>
        );
      })()}

      {/* ---- Roster v2: grouped lifecycle (signed up → checked in → on bus), ---- */}
      {/* ---- chaperones on the roster, add-as-signed-up, remove/restore.    ---- */}
      {reportEvent && (() => {
        const all = reportRows;
        const active = all.filter((r) => r.status !== "cancelled");
        const removed = all.filter((r) => r.status === "cancelled")
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        const chaperones = active.filter((r) => r.chaperone === true)
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        const pax = active.filter((r) => r.chaperone !== true);
        const onBus = pax.filter((r) => r.present && r.boarded)
          .sort((a, b) => (a.boardedAt?.toMillis?.() || 0) - (b.boardedAt?.toMillis?.() || 0));
        const checkedIn = pax.filter((r) => r.present && !r.boarded)
          .sort((a, b) => (a.presentAt?.toMillis?.() || 0) - (b.presentAt?.toMillis?.() || 0));
        const signedUp = pax.filter((r) => r.status === "confirmed" && !r.present)
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        const waitlisted = pax.filter((r) => r.status === "waitlisted")
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

        // Capacity gates confirmed STUDENTS only (chaperones/staff don't count).
        const confirmedStudents = onBus.length + checkedIn.length + signedUp.length;
        const cap = reportEvent.capacity;
        const seatsLeft = cap != null ? Math.max(0, cap - confirmedStudents) : null;

        // Manual search matches (client-side, starts at 2 chars).
        const q = search.trim().toLowerCase();
        const matches = q.length < 2 ? [] : students.filter((s) =>
          s.name.toLowerCase().includes(q) || s.email.includes(q) || (s.sisId && s.sisId.includes(q))
        ).slice(0, 8);
        const rowFor = (s) => active.find((r) =>
          (r.email && s.email && r.email.toLowerCase() === s.email) ||
          (r.studentId && s.id && String(r.studentId) === String(s.id))
        );
        const addArgs = (s) => ({ student: { studentId: s.id, name: s.name, email: s.email, sisId: s.sisId } });

        // Forward + remove actions for a roster row, shown by current state.
        const rowActions = (r) => (
          <>
            {!r.present && (
              <button style={miniBtn} disabled={manualBusy === `ci:${r.id}`}
                onClick={() => markManual({ signupId: r.id, checkpoint: "checkin" }, `ci:${r.id}`)}>
                {manualBusy === `ci:${r.id}` ? "…" : "Bus #1"}
              </button>
            )}
            {!r.boarded && (
              <button style={{ ...miniBtn, borderColor: KA_RED, color: KA_RED }} disabled={manualBusy === `bd:${r.id}`}
                onClick={() => markManual({ signupId: r.id, checkpoint: "board" }, `bd:${r.id}`)}>
                {manualBusy === `bd:${r.id}` ? "…" : "Return"}
              </button>
            )}
            <button style={{ ...miniBtn, borderColor: "#ccc", color: "#999" }} disabled={manualBusy === `rm:${r.id}`}
              onClick={() => removeRow(r.id, r.name || r.email, `rm:${r.id}`)}>
              {manualBusy === `rm:${r.id}` ? "…" : "Remove"}
            </button>
          </>
        );

        return (
          <div style={overlay} onClick={() => setReportEvent(null)}>
            <div style={modal} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ margin: 0, color: KA_NAVY }}>{reportEvent.name} — Roster</h3>
                <button onClick={() => setReportEvent(null)} style={ghost}>Close</button>
              </div>
              <div style={{ fontSize: 13, color: KA_NAVY, fontWeight: 600, margin: "6px 0 2px" }}>
                {cap != null
                  ? <>Capacity: {cap} student{cap === 1 ? "" : "s"}
                      <span style={{ color: seatsLeft === 0 ? KA_RED : "#1a7f37", marginLeft: 6 }}>
                        · {seatsLeft === 0 ? "full" : `${seatsLeft} seat${seatsLeft === 1 ? "" : "s"} left`}
                      </span>
                      {waitlisted.length ? <span style={{ color: KA_GOLD }}> · {waitlisted.length} waitlisted</span> : null}
                    </>
                  : <>Capacity: unlimited</>}
              </div>
              <div style={{ fontSize: 13, color: "#555", margin: "2px 0 14px" }}>
                <strong style={{ color: "#1a7f37" }}>{onBus.length}</strong> on return bus
                {` · ${checkedIn.length} on Bus #1 · ${signedUp.length} signed up`}
                {waitlisted.length ? ` · ${waitlisted.length} waitlisted` : ""}
                {` · ${chaperones.length} chaperone${chaperones.length === 1 ? "" : "s"}`}
                <span style={{ color: "#aaa" }}> · live</span>
              </div>

              {/* Add a student to the roster: sign up ahead, or check in / board a walk-up */}
              <div style={{ border: `1px solid ${KA_NAVY}`, borderRadius: 10, padding: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: KA_NAVY, marginBottom: 4 }}>
                  Add a student to the roster
                </div>
                <div style={{ fontSize: 12, color: "#777", marginBottom: 8 }}>
                  Sign a student up ahead of time, or put a walk-up on Bus #1 / the return bus if they can't scan. Start typing a name.
                </div>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={studentsLoaded ? "Search students by name, email, or ID…" : "Loading students…"}
                  disabled={!studentsLoaded}
                  style={inp}
                />
                {q.length >= 2 && matches.length === 0 && (
                  <div style={{ fontSize: 13, color: "#999", marginTop: 8 }}>No matching students.</div>
                )}
                {matches.length > 0 && (
                  <div style={{ display: "grid", gap: 4, marginTop: 8 }}>
                    {matches.map((s) => {
                      const r = rowFor(s);
                      const state = r ? (r.boarded ? "on return bus" : r.present ? "on Bus #1" : r.status === "waitlisted" ? "waitlisted" : "signed up") : "";
                      return (
                        <div key={s.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, background: "#fafafa", borderRadius: 8, padding: "6px 10px" }}>
                          <span style={{ minWidth: 0, overflow: "hidden" }}>
                            <span style={{ color: KA_NAVY, fontSize: 14 }}>{s.name}</span>
                            {s.grade ? <span style={{ color: "#999", fontSize: 12 }}> · {s.grade}</span> : null}
                            {state ? <span style={{ fontSize: 11, marginLeft: 6, color: state === "on return bus" ? "#1a7f37" : KA_GOLD }}>· {state}</span> : null}
                          </span>
                          <span style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                            <button style={miniBtn} disabled={manualBusy === `su:${s.id}`}
                              onClick={() => markManual({ ...addArgs(s), checkpoint: "signup" }, `su:${s.id}`)}>
                              {manualBusy === `su:${s.id}` ? "…" : "Sign up"}
                            </button>
                            <button style={miniBtn} disabled={manualBusy === `c:${s.id}`}
                              onClick={() => markManual({ ...addArgs(s), checkpoint: "checkin" }, `c:${s.id}`)}>
                              {manualBusy === `c:${s.id}` ? "…" : "Bus #1"}
                            </button>
                            <button style={{ ...miniBtn, borderColor: KA_RED, color: KA_RED }} disabled={manualBusy === `b:${s.id}`}
                              onClick={() => markManual({ ...addArgs(s), checkpoint: "board" }, `b:${s.id}`)}>
                              {manualBusy === `b:${s.id}` ? "…" : "Return"}
                            </button>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Add a chaperone */}
              <div style={{ border: `1px solid ${KA_GOLD}`, borderRadius: 10, padding: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "#7a6a3f", marginBottom: 6 }}>
                  Add a chaperone
                </div>
                <input
                  value={chapAddSearch}
                  onChange={(e) => setChapAddSearch(e.target.value)}
                  placeholder={staffLoaded ? "Search staff by name or email…" : "Loading staff…"}
                  disabled={!staffLoaded || chapAddBusy}
                  style={inp}
                />
                {(() => {
                  const cq = chapAddSearch.trim().toLowerCase();
                  if (cq.length < 2) return null;
                  const already = new Set(active.filter((r) => r.chaperone).map((r) => (r.email || "").toLowerCase()));
                  const cmatches = staff.filter((s) =>
                    !already.has(s.email) && (s.name.toLowerCase().includes(cq) || s.email.includes(cq))
                  ).slice(0, 6);
                  if (cmatches.length === 0) {
                    return <div style={{ fontSize: 13, color: "#999", marginTop: 8 }}>No matching staff.</div>;
                  }
                  return (
                    <div style={{ display: "grid", gap: 4, marginTop: 8 }}>
                      {cmatches.map((s) => (
                        <button key={s.id} type="button" disabled={chapAddBusy}
                          onClick={() => addChaperoneToRoster(s)} style={pickRow}>
                          <span style={{ color: KA_NAVY }}>{s.name}</span>
                          <span style={{ color: "#999", fontSize: 12, marginLeft: 8 }}>{s.email}</span>
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {checkedIn.length > 0 && (
                <div style={{ background: "rgba(232,53,48,0.08)", border: `1px solid ${KA_RED}`, borderRadius: 10, padding: "8px 12px", fontSize: 13, color: KA_RED, marginBottom: 6 }}>
                  ⚠ {checkedIn.length} on Bus #1 <strong>haven't scanned return boarding</strong> — confirm before you head back.
                </div>
              )}

              {chaperones.length > 0 && (
                <Section title={`Chaperones (${chaperones.length})`} color={KA_GOLD}>
                  {chaperones.map((r) => (
                    <Line key={r.id} name={r.name} email={r.email} role="staff" manual={r.manual}
                      right={r.boarded ? "on return bus" : r.present ? "on Bus #1" : ""}
                      action={rowActions(r)} />
                  ))}
                </Section>
              )}

              <Section title={`On return bus (${onBus.length})`} color="#1a7f37">
                {onBus.length === 0
                  ? <Empty text="No one on the return bus yet." />
                  : onBus.map((r) => <Line key={r.id} name={r.name} email={r.email} role={r.role} manual={r.manual} right={fmt(r.boardedAt)} action={rowActions(r)} />)}
              </Section>

              <Section title={`On Bus #1 (${checkedIn.length})`} color={KA_RED}>
                {checkedIn.length === 0
                  ? <Empty text="No one is on Bus #1 right now." />
                  : checkedIn.map((r) => <Line key={r.id} name={r.name} email={r.email} role={r.role} manual={r.manual} right={fmt(r.presentAt)} action={rowActions(r)} />)}
              </Section>

              <Section title={`Signed up — not here yet (${signedUp.length})`} color={KA_NAVY}>
                {signedUp.length === 0
                  ? <Empty text="No one is signed up and waiting." />
                  : signedUp.map((r) => <Line key={r.id} name={r.name} email={r.email} role={r.role} manual={r.manual} right="" action={rowActions(r)} />)}
              </Section>

              {waitlisted.length > 0 && (
                <Section title={`Waitlisted (${waitlisted.length})`} color="#888">
                  {waitlisted.map((r) => (
                    <Line key={r.id} name={r.name} email={r.email} role={r.role} right="waitlist"
                      action={
                        <>
                          <button style={miniBtn} disabled={manualBusy === `su:${r.id}`}
                            onClick={() => markManual({ signupId: r.id, checkpoint: "signup" }, `su:${r.id}`)}>
                            {manualBusy === `su:${r.id}` ? "…" : "Sign up"}
                          </button>
                          <button style={{ ...miniBtn, borderColor: "#ccc", color: "#999" }} disabled={manualBusy === `rm:${r.id}`}
                            onClick={() => removeRow(r.id, r.name || r.email, `rm:${r.id}`)}>
                            {manualBusy === `rm:${r.id}` ? "…" : "Remove"}
                          </button>
                        </>
                      } />
                  ))}
                </Section>
              )}

              {removed.length > 0 && (
                <Section title={`Removed (${removed.length})`} color="#aaa">
                  {removed.map((r) => (
                    <Line key={r.id} name={r.name} email={r.email} role={r.role} right="removed"
                      action={
                        <button style={miniBtn} disabled={manualBusy === `re:${r.id}`}
                          onClick={() => markManual({ signupId: r.id, checkpoint: "signup" }, `re:${r.id}`)}>
                          {manualBusy === `re:${r.id}` ? "…" : "Restore"}
                        </button>
                      } />
                  ))}
                </Section>
              )}

              <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
                <button onClick={() => openQr(reportEvent, "checkin")} style={ghost}>Bus #1 QR</button>
                <button onClick={() => openQr(reportEvent, "board")} style={{ ...ghost, color: KA_RED, borderColor: KA_RED }}>Return boarding QR</button>
                <button onClick={() => downloadSheet(reportEvent)} style={ghost} disabled={pdfBusy === `${reportEvent.id}:sheet`}>
                  {pdfBusy === `${reportEvent.id}:sheet` ? "…" : "Sign-in sheet (PDF)"}
                </button>
                <button onClick={() => downloadBoardingCard(reportEvent)} style={{ ...ghost, color: KA_RED, borderColor: KA_RED }} disabled={pdfBusy === `${reportEvent.id}:card`}>
                  {pdfBusy === `${reportEvent.id}:card` ? "…" : "Return card (PDF)"}
                </button>
                <button onClick={() => downloadBarcodes(reportEvent)} style={ghost}>Barcode sheet (PDF)</button>
                {reportEvent.soc && (
                  <button onClick={() => sendToSoc(reportEvent)} style={{ ...ghost, background: KA_GOLD, color: "#fff", borderColor: KA_GOLD }} disabled={socBusy}>
                    {socBusy ? "Sending…" : "Send sheet to SOC"}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ---- Edit event ---- */}
      {editEvent && editForm && (
        <div style={overlay} onClick={() => !editBusy && setEditEvent(null)}>
          <div style={modal} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h3 style={{ margin: 0, color: KA_NAVY }}>Edit — {editEvent.name}</h3>
              <button onClick={() => setEditEvent(null)} style={ghost} disabled={editBusy}>Close</button>
            </div>

            <L label="Event name">
              <input value={editForm.name} onChange={setEdit("name")} style={inp} />
            </L>
            <L label="Description">
              <textarea value={editForm.description} onChange={setEdit("description")} rows={2} style={{ ...inp, resize: "vertical" }} />
            </L>
            <div style={grid3}>
              <L label="Starts"><input type="datetime-local" value={editForm.startAt} onChange={setEdit("startAt")} style={inp} /></L>
              <L label="Ends"><input type="datetime-local" value={editForm.endAt} onChange={setEdit("endAt")} style={inp} /></L>
              <L label="Chat expires"><input type="datetime-local" value={editForm.chatExpiryAt} onChange={setEdit("chatExpiryAt")} style={inp} /></L>
            </div>
            <div style={grid2}>
              <L label="Capacity (blank = unlimited)">
                <input type="number" min="1" value={editForm.capacity} onChange={setEdit("capacity")} style={inp} />
              </L>
              <L label="Grad years allowed (blank = all)">
                <input value={editForm.gradYears} onChange={setEdit("gradYears")} placeholder="e.g. 27 28" style={inp} />
              </L>
            </div>
            <L label="Chaperones (type a name)">
              <ChaperonePicker
                staff={staff}
                staffLoaded={staffLoaded}
                selected={editChaperones}
                search={editSearch}
                setSearch={setEditSearch}
                onAdd={(c) => {
                  const email = (c.email || "").toLowerCase();
                  if (!email) return;
                  setEditChaperones((l) => l.some((x) => x.email === email) ? l : [...l, { email, name: c.name || "" }]);
                  setEditSearch("");
                }}
                onRemove={(email) => setEditChaperones((l) => l.filter((x) => x.email !== email))}
              />
            </L>
            <L label="SOC — School Officer in Charge (gate sign-out)">
              <SocPicker
                staff={staff}
                staffLoaded={staffLoaded}
                selected={editSoc}
                search={editSocSearch}
                setSearch={setEditSocSearch}
                onPick={(c) => { setEditSoc({ email: (c.email || "").toLowerCase(), name: c.name || "" }); setEditSocSearch(""); }}
                onClear={() => setEditSoc(null)}
              />
            </L>

            {editError && <div style={{ color: KA_RED, fontSize: 14, margin: "6px 0" }}>{editError}</div>}

            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button onClick={saveEdit} disabled={editBusy} style={{ ...btn, background: editBusy ? "#aaa" : KA_RED }}>
                {editBusy ? "Saving…" : "Save changes"}
              </button>
              <button onClick={() => setEditEvent(null)} disabled={editBusy} style={ghost}>Cancel</button>
            </div>
            <p style={{ fontSize: 12, color: "#999", marginTop: 10 }}>
              Removing a chaperone here updates the list, but doesn't pull their existing roster row or chat access —
              use the roster's Remove for that. Newly added chaperones are emailed and added to the chat.
            </p>
          </div>
        </div>
      )}

      {reportsOpen && <EventReports onClose={() => setReportsOpen(false)} />}
    </div>
  );
}

function Section({ title, color, children }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: color || KA_NAVY, marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ display: "grid", gap: 4 }}>{children}</div>
    </div>
  );
}

function Line({ name, email, role, right, action, manual }) {
  const isStudent = role !== "staff";
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 14, padding: "6px 10px", background: "#fafafa", borderRadius: 8 }}>
      <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        <span style={{ color: KA_NAVY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name || email}</span>
        <span style={{
          fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4,
          padding: "2px 6px", borderRadius: 6, flexShrink: 0,
          background: isStudent ? "rgba(35,60,95,0.08)" : "rgba(162,145,100,0.20)",
          color: isStudent ? KA_NAVY : "#7a6a3f",
        }}>
          {isStudent ? "Student" : "Teacher"}
        </span>
        {manual ? <span style={{ fontSize: 10, color: "#999", flexShrink: 0 }} title="Entered manually">✎</span> : null}
      </span>
      <span style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0, marginLeft: 8 }}>
        {right ? <span style={{ fontSize: 12, color: "#777" }}>{right}</span> : null}
        {action}
      </span>
    </div>
  );
}

function Empty({ text }) {
  return <div style={{ fontSize: 13, color: "#999", padding: "4px 2px" }}>{text}</div>;
}

// Chaperone picker: type-ahead against /staff, selected shown as chips. Allows
// adding a typed @kingsacademy.edu.jo address directly (Enter) for edge cases.
function ChaperonePicker({ staff, staffLoaded, selected, search, setSearch, onAdd, onRemove }) {
  const q = search.trim().toLowerCase();
  const selectedEmails = new Set(selected.map((s) => s.email));
  const matches = q.length < 2 ? [] : staff.filter((s) =>
    !selectedEmails.has(s.email) && (s.name.toLowerCase().includes(q) || s.email.includes(q))
  ).slice(0, 8);
  const canAddTyped = /@kingsacademy\.edu\.jo$/i.test(q) && !selectedEmails.has(q);
  return (
    <div>
      {selected.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6, marginBottom: 6 }}>
          {selected.map((c) => (
            <span key={c.email} style={chip}>
              {c.name || c.email}
              <button type="button" onClick={() => onRemove(c.email)} style={chipX} aria-label="Remove">×</button>
            </span>
          ))}
        </div>
      )}
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (matches[0]) onAdd(matches[0]);
            else if (canAddTyped) onAdd({ email: q, name: "" });
          }
        }}
        placeholder={staffLoaded ? "Search staff by name or email…" : "Loading staff…"}
        disabled={!staffLoaded}
        style={inp}
      />
      {matches.length > 0 && (
        <div style={{ display: "grid", gap: 4, marginTop: 6 }}>
          {matches.map((s) => (
            <button key={s.id} type="button" onClick={() => onAdd(s)} style={pickRow}>
              <span style={{ color: KA_NAVY }}>{s.name}</span>
              <span style={{ color: "#999", fontSize: 12, marginLeft: 8 }}>{s.email}</span>
            </button>
          ))}
        </div>
      )}
      {matches.length === 0 && canAddTyped && (
        <button type="button" onClick={() => onAdd({ email: q, name: "" })} style={{ ...pickRow, marginTop: 6 }}>
          Add “{q}”
        </button>
      )}
    </div>
  );
}

// Single-select staff picker for the SOC. Shows a chip once chosen.
function SocPicker({ staff, staffLoaded, selected, search, setSearch, onPick, onClear }) {
  const q = search.trim().toLowerCase();
  const matches = q.length < 2 ? [] : staff.filter((s) =>
    s.name.toLowerCase().includes(q) || s.email.includes(q)
  ).slice(0, 8);
  const canAddTyped = /@kingsacademy\.edu\.jo$/i.test(q);
  if (selected) {
    return (
      <div style={{ marginTop: 6 }}>
        <span style={chip}>
          {selected.name || selected.email}
          <button type="button" onClick={onClear} style={chipX} aria-label="Remove">×</button>
        </span>
      </div>
    );
  }
  return (
    <div>
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (matches[0]) onPick(matches[0]);
            else if (canAddTyped) onPick({ email: q, name: "" });
          }
        }}
        placeholder={staffLoaded ? "Search staff by name or email…" : "Loading staff…"}
        disabled={!staffLoaded}
        style={inp}
      />
      {matches.length > 0 && (
        <div style={{ display: "grid", gap: 4, marginTop: 6 }}>
          {matches.map((s) => (
            <button key={s.id} type="button" onClick={() => onPick(s)} style={pickRow}>
              <span style={{ color: KA_NAVY }}>{s.name}</span>
              <span style={{ color: "#999", fontSize: 12, marginLeft: 8 }}>{s.email}</span>
            </button>
          ))}
        </div>
      )}
      {matches.length === 0 && canAddTyped && (
        <button type="button" onClick={() => onPick({ email: q, name: "" })} style={{ ...pickRow, marginTop: 6 }}>
          Add “{q}”
        </button>
      )}
    </div>
  );
}

function L({ label, children }) {
  return (
    <label style={{ display: "block", fontSize: 13, color: KA_NAVY, marginBottom: 12 }}>
      {label}
      {children}
    </label>
  );
}

const card = { background: "#fafafa", border: "1px solid #eee", borderRadius: 12, padding: 20, marginTop: 16 };
const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 };
const grid3 = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 };
const inp = { display: "block", marginTop: 6, padding: "10px 12px", border: "1px solid #ccc", borderRadius: 8, fontSize: 16, width: "100%", boxSizing: "border-box" };
const btn = { color: "#fff", border: "none", borderRadius: 8, padding: "11px 20px", fontSize: 14, cursor: "pointer", marginTop: 4 };
const ghost = { background: "#fff", color: KA_NAVY, border: `1px solid ${KA_NAVY}`, borderRadius: 8, padding: "8px 14px", fontSize: 13, cursor: "pointer", textDecoration: "none" };
const miniBtn = { background: "#fff", color: KA_NAVY, border: `1px solid ${KA_NAVY}`, borderRadius: 7, padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" };
const chip = { display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(35,60,95,0.08)", color: KA_NAVY, borderRadius: 16, padding: "4px 10px", fontSize: 13 };
const chipX = { border: "none", background: "transparent", color: KA_RED, cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0 };
const pickRow = { display: "block", width: "100%", textAlign: "left", background: "#fafafa", border: "1px solid #eee", borderRadius: 8, padding: "8px 10px", fontSize: 14, cursor: "pointer" };
const toggleBtn = { background: "#fff", color: "#666", border: "1px solid #ccc", borderRadius: 20, padding: "6px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" };
const row = { display: "flex", justifyContent: "space-between", alignItems: "center", border: "1px solid #eee", borderRadius: 10, padding: "12px 16px", gap: 12, flexWrap: "wrap" };
const roleBadge = (color) => ({
  fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4,
  padding: "2px 7px", borderRadius: 6, color: "#fff", background: color,
});
const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 200 };
const modal = { background: "#fff", borderRadius: 14, padding: 20, width: "100%", maxWidth: 560, maxHeight: "85vh", overflowY: "auto", boxShadow: "0 12px 40px rgba(0,0,0,0.2)" };